import { useQuery } from '@common/hooks';
import React from 'react';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import {
  filterableColumnBuilder,
  normalizeSortingData,
  SelectedFilters,
} from 'works/legacy/lib/table-filters-helper';
import { FilteredTable } from 'works/components/filtered-table';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  Link,
  AddButton,
  AppContent,
  ErrorHandler,
  Text,
  TimeWithTimeZone,
} from '@common/components';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import {
  WorkforceGroupFiltersInput,
  WorkforceGroupOrdersInput,
} from '@common/graphql';
import { TableProps } from 'antd';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { buildDialogUrl } from '@common/services';
import {
  WORKFORCE_GROUPS_QUERY,
  WorkforceGroupType,
} from '@scheduling/graphql';
import { AdminWorkforceGroupsTableAction } from './_admin-workforce-groups-table-action';
import { convertToType } from '@scheduling/services';
import { SorterResult } from 'antd/lib/table/interface';
import moment from 'moment';

// Extended interface to allow using nested paths as column keys
type ExtendedWorkforceGroupType = WorkforceGroupType & {
  'defaultConfig.earliestAdvanceTime': number;
  'defaultConfig.earliestAdvanceTimeFormat': 'days' | 'hours';
  'defaultConfig.openShiftsVisibilityDelayHours': number;
  'defaultConfig.openShiftsVisibilityDelayHoursFormat': 'days' | 'hours';
  'defaultConfig.gateEarliestAdvanceTime': boolean;
  'defaultConfig.gateOpenShiftsVisibility': boolean;
  'defaultConfig.updatedAt': string;
};

const LOCALSTORAGE_FILTER_KEY = 'admin-workforce-groups-filter';

export function AdminWorkforceGroupsPage() {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.GROUPS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE,
    },
  ]);

  const [selectedFilters, setSelectedFilters] = useStoredFilters(
    LOCALSTORAGE_FILTER_KEY,
    {
      store: localStorageStore,
      defaults: {
        active: [true],
      } as WorkforceGroupFiltersInput,
    }
  );

  const { data, loading, error, fetchMore } = useQuery(WORKFORCE_GROUPS_QUERY, {
    variables: {
      workforceGroupInput: {
        filters: selectedFilters,
      },
    },
  });

  if (error) {
    return <ErrorHandler error={error} log="admin-workforce-groups-page" />;
  }

  const groupOrDefaultSettingsLastUpdate = (group: WorkforceGroupType) => {
    const groupUpdatedAt = moment(group.updatedAt);
    const defaultConfigUpdatedAt = moment(group.defaultConfig?.updatedAt);
    const lastUpdate =
      groupUpdatedAt > defaultConfigUpdatedAt
        ? groupUpdatedAt
        : defaultConfigUpdatedAt;

    return lastUpdate;
  };

  // Use the extended type for column definitions
  const column = filterableColumnBuilder<ExtendedWorkforceGroupType>({
    paginated: false,
    filterConfig: {
      selectedFilters:
        selectedFilters as SelectedFilters<ExtendedWorkforceGroupType>,
    },
  });

  const columns = [
    column({
      title: 'Group Name',
      key: 'name',
      sorter: true,
      filterDropdown: TextSearchTableFilter,
      render: (value, record) => (
        <Link
          id={record.id}
          to={`${ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE}/${record.id}/details`}
          className="text-heavy"
        >
          {value}
        </Link>
      ),
    }),
    column({
      title: 'Staff',
      key: 'activeWorkersCount',
      width: '5%',
      render: (value) => value.toString(),
    }),
    column({
      title: 'Advance Shift Visibility',
      key: 'defaultConfig.earliestAdvanceTime',
      render: (_, record) => {
        const config = record.defaultConfig;
        if (!config?.gateEarliestAdvanceTime) return;

        const value = config.earliestAdvanceTime;
        const format = config.earliestAdvanceTimeFormat || 'hours';
        const formattedValue = convertToType(
          String(value),
          'hours',
          format as 'hours' | 'days'
        );
        const formattedFormat =
          formattedValue.toString() === '1' ? format.slice(0, -1) : format; // singular if 1
        return `${formattedValue} ${formattedFormat}`;
      },
    }),
    column({
      title: 'Schedule Release Delay',
      key: 'defaultConfig.openShiftsVisibilityDelayHours',
      render: (_, record) => {
        const config = record.defaultConfig;
        if (!config?.gateOpenShiftsVisibility) return;

        const value = config.openShiftsVisibilityDelayHours;
        const format = config.openShiftsVisibilityDelayHoursFormat || 'hours';
        const formattedValue = convertToType(
          String(value),
          'hours',
          format as 'hours' | 'days'
        );
        const formattedFormat =
          formattedValue.toString() === '1' ? format.slice(0, -1) : format; // singular if 1
        return `${formattedValue} ${formattedFormat}`;
      },
    }),
    column({
      title: 'Status',
      key: 'active',
      width: '10%',
      defaultFilteredValue: [true],
      filters: [
        {
          text: 'Active',
          value: true,
        },
        {
          text: 'Archived',
          value: false,
        },
      ],
      render: (_, record) => {
        if (!record.active) {
          return (
            <Text weight="bold" color="midnightLighter">
              Archived
            </Text>
          );
        }
        return (
          <Text weight="bold" color="meadowDarker">
            Active
          </Text>
        );
      },
    }),
    column({
      title: 'Last Updated',
      key: 'updatedAt',
      width: '25%',
      sorter: (a, b) => {
        return groupOrDefaultSettingsLastUpdate(a).diff(
          groupOrDefaultSettingsLastUpdate(b)
        );
      },
      render: (_, record) => {
        return (
          <TimeWithTimeZone
            date={groupOrDefaultSettingsLastUpdate(record)}
            variant="long_format"
          />
        );
      },
    }),
    column({
      title: 'Action',
      key: 'id',
      width: '5%',
      sorter: false,
      render: (_, record) => (
        <AdminWorkforceGroupsTableAction
          recordId={record.id}
          isRecordActive={record.active}
          hasActiveWorkers={record.activeWorkersCount > 0}
        />
      ),
    }),
  ];

  // Remap nested path keys to their parent keys for the API
  const remapOrders = (
    orders:
      | SorterResult<ExtendedWorkforceGroupType>
      | SorterResult<ExtendedWorkforceGroupType>[]
  ) => {
    return Array.isArray(orders)
      ? orders.map((order) => {
          const columnKey = order.columnKey?.toString() || '';
          if (columnKey.startsWith('defaultConfig.')) {
            return { ...order, columnKey: 'defaultConfig' };
          }
          return order;
        })
      : orders;
  };

  const handleTableChange: TableProps<ExtendedWorkforceGroupType>['onChange'] =
    (_pagination, filters, orders) => {
      const remappedOrders = remapOrders(orders);

      fetchMore({
        variables: {
          workforceGroupInput: {
            filters: filters as WorkforceGroupFiltersInput,
            orders: normalizeSortingData(
              remappedOrders
            ) as WorkforceGroupOrdersInput,
          },
        },
        updateQuery: (_prev, { fetchMoreResult }) => {
          return fetchMoreResult || _prev;
        },
      });
    };

  return (
    <>
      <AppContent>
        <FilteredTable
          rowKey="id"
          className="admin-workforce-groups-page__table"
          onChange={handleTableChange}
          loading={loading}
          columns={columns}
          dataSource={data?.workforceGroups as ExtendedWorkforceGroupType[]}
          pagination={{
            hideOnSinglePage: true,
            defaultPageSize: 100000,
          }}
          onFilterChange={setSelectedFilters}
          localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
          filters={selectedFilters}
          scroll={{ x: '100%' }}
          headerRightSlot={
            <Link to={buildDialogUrl('workforce-group-create-drawer')}>
              <AddButton>Group</AddButton>
            </Link>
          }
        />
        {!!data?.workforceGroups.length && (
          <Text color="midnightRegular">
            Showing all the {data?.workforceGroups.length} groups
          </Text>
        )}
      </AppContent>
      <style jsx>{`
        :global(.admin-workforce-groups-page__table) {
          margin-bottom: 16px;
        }
        :global(
          .admin-workforce-groups-page__table .ant-table-tbody > tr > td
        ) {
          padding: 14px 4px 14px 8px;
        }
      `}</style>
    </>
  );
}
